import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { CheckoutVariantStatus } from "@fitness-app/data-models/entities/CheckoutVariant";
import { type SalesFunnel } from "@fitness-app/data-models/entities/SalesFunnel";

import { productApi, supabase } from "~/store/initializeStore";
import { duplicateSalesFunnelSteps } from "../components/duplicateSalesFunnel";
import { type SalesFunnelFormModel } from "../components/ProductSalesFunnelForm";

// Configure dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

type StepStats = {
  viewed: number;
  purchased: number;
  rejected: number;
  revenue: number;
  conversion: number;
};

type FunnelStatsResponse = {
  all: Record<string, StepStats>;
  testmode: Record<string, StepStats>;
};

export const useSalesFunnels = (
  productId: string,
  excludeTestEvents = false,
  dateRange?: { startDate?: string; endDate?: string },
) => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editedModel, setEditedModel] = useState<SalesFunnel | null>(null);
  const [showSavingIndicator, setShowSavingIndicator] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isError } = useQuery(["salesFunnels", productId], {
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_funnel")
        .select("*")
        .in("status", [CheckoutVariantStatus.Active, CheckoutVariantStatus.Inactive])
        .eq("productId", productId)
        .order("createdAt", { ascending: false })
        .limit(100)
        .returns<SalesFunnel[]>();

      if (error) {
        throw new Error(error.message);
      }

      if (!data) {
        return [];
      }

      return data;
    },
  });

  const funnelsIds = useMemo(() => data?.map((funnel) => funnel.id) || [], [data]);

  const {
    data: funnelStatsData,
    isLoading: statsIsLoading,
    refetch: statsRefetch,
    isError: statsIsError,
  } = useQuery(["salesFunnelsStats", ...funnelsIds, dateRange?.startDate, dateRange?.endDate], {
    queryFn: async () => {
      if (funnelsIds.length === 0) {
        return {};
      }

      // Fetch stats for each funnel using the new endpoint
      const statsPromises = funnelsIds.map(async (funnelId) => {
        try {
          const response = await productApi.get<FunnelStatsResponse>("/funnel/stats", {
            params: {
              salesFunnelId: funnelId,
              ...(dateRange?.startDate && { startDate: dateRange.startDate }),
              ...(dateRange?.endDate && { endDate: dateRange.endDate }),
            },
          });
          return { funnelId, ...response.data };
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(statsPromises);

      // Filter out null results
      const validResults = results.filter(
        (result): result is { funnelId: string; all: Record<string, StepStats>; testmode: Record<string, StepStats> } =>
          result !== null,
      );

      if (validResults.length === 0) {
        return {};
      }

      // Transform the Redis-based stats to the expected format
      const funnelStats: Record<
        string,
        {
          viewed: number;
          purchased: number;
          rejected: number;
          revenue: number;
          stats: Record<string, StepStats>;
          testmodeStats: Record<string, StepStats>;
        }
      > = {};

      for (const result of validResults) {
        const { funnelId, all, testmode } = result;

        // Calculate totals for production stats
        let totalViewed = 0;
        let totalPurchased = 0;
        let totalRejected = 0;
        let totalRevenue = 0;

        // Process production stats
        for (const [_stepId, stepStats] of Object.entries(all)) {
          totalViewed += stepStats.viewed;
          totalPurchased += stepStats.purchased;
          totalRejected += stepStats.rejected;
          totalRevenue += stepStats.revenue;
        }

        // Process test mode stats based on excludeTestEvents flag
        for (const [_stepId, stepStats] of Object.entries(testmode)) {
          if (excludeTestEvents) {
            // When excluding test events, subtract test mode stats from totals (which already include test events)
            totalViewed = Math.max(0, totalViewed - stepStats.viewed);
            totalPurchased = Math.max(0, totalPurchased - stepStats.purchased);
            totalRejected = Math.max(0, totalRejected - stepStats.rejected);
            totalRevenue = Math.max(0, totalRevenue - stepStats.revenue);
          }
          // When includeTestEvents is true, we use the totals as-is (they already include test events)
        }

        funnelStats[funnelId] = {
          viewed: totalViewed,
          purchased: totalPurchased,
          rejected: totalRejected,
          revenue: totalRevenue,
          stats: all,
          testmodeStats: testmode,
        };
      }

      return funnelStats;
    },
    enabled: funnelsIds.length > 0,
  });

  const serializeFunnel = (model: SalesFunnelFormModel) => ({
    activeUntil: model.activeUntil,
    cartSettings: model.cartSettings,
    default: model.default,
    excluded: model.excluded,
    metadata: model.metadata,
    name: model.name,
    productId,
    slug: model.slug,
    status: model.status,
    steps: model.steps as any,
    target: model.target,
    updatedAt: new Date().toISOString(),
  });

  const saveFunnel = async (model: SalesFunnelFormModel, funnelId?: string) => {
    setShowSavingIndicator(true);
    try {
      const payload = serializeFunnel(model);
      const response = funnelId
        ? await supabase.from("sales_funnel").update(payload).eq("id", funnelId).select("*").single()
        : await supabase
            .from("sales_funnel")
            .insert({
              ...payload,
              createdAt: new Date().toISOString(),
              purchase: 0,
              revenue: 0,
              views: 0,
            })
            .select("*")
            .single();

      if (response.error) {
        throw new Error(response.error.message);
      }

      await queryClient.invalidateQueries(["salesFunnels", productId]);
      setShowFormModal(false);
      setEditedModel(null);
      message.success(funnelId ? "Sales funnel zaktualizowany" : "Sales funnel utworzony");
      return "success";
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się zapisać sales funnelu");
      return "error";
    } finally {
      setShowSavingIndicator(false);
    }
  };

  /** Copies the funnel under a new slug, inactive, so it can be reviewed before going live. */
  const duplicateFunnel = async (source: SalesFunnel, slug: string) => {
    setShowSavingIndicator(true);
    try {
      const now = new Date().toISOString();
      // The whole row, so columns the form does not know about (configuration, cartSettings) survive.
      const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...row } = source;
      const { error } = await supabase.from("sales_funnel").insert({
        ...(row as any),
        createdAt: now,
        updatedAt: now,
        default: false,
        name: `${source.name} (kopia)`,
        purchase: 0,
        revenue: 0,
        views: 0,
        slug,
        status: CheckoutVariantStatus.Inactive,
        steps: duplicateSalesFunnelSteps(source.steps) as any,
      });

      if (error) {
        throw new Error(error.code === "23505" ? `Slug „${slug}” jest już zajęty` : error.message);
      }

      await queryClient.invalidateQueries(["salesFunnels", productId]);
      message.success("Sales funnel zduplikowany");
      return "success";
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Nie udało się zduplikować sales funnelu");
      return "error";
    } finally {
      setShowSavingIndicator(false);
    }
  };

  const archiveFunnel = async (funnelId: string) => {
    const { error } = await supabase
      .from("sales_funnel")
      .update({ status: CheckoutVariantStatus.Archived, updatedAt: new Date().toISOString() })
      .eq("id", funnelId);

    if (error) {
      message.error(error.message);
      return "error";
    }

    await queryClient.invalidateQueries(["salesFunnels", productId]);
    message.success("Sales funnel zarchiwizowany");
    return "success";
  };

  const setEditedItem = (item: SalesFunnel) => {
    setEditedModel(item);
    setShowFormModal(true);
  };

  const statsByFunnel = useMemo(() => {
    if (!funnelStatsData) {
      return {};
    }

    // The data is already in the correct format from the new endpoint
    return funnelStatsData;
  }, [funnelStatsData]);

  return {
    data,
    isLoading,
    refetch,
    isError,
    setShowFormModal,
    showFormModal,
    editedModel,
    setEditedModel,
    setEditedItem,
    showSavingIndicator,
    saveFunnel,
    duplicateFunnel,
    archiveFunnel,
    statsData: funnelStatsData,
    statsIsLoading: funnelsIds.length > 0 && statsIsLoading,
    statsRefetch,
    statsIsError,
    statsByFunnel,
  };
};
